import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Form, Image, Row, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { CustomForm, CustomModal } from "../../components";
import {
  useCreateNillNewsMutation,
  useDeleteNillNewsMutation,
  useFetchAllNillNewsQuery,
  useFilterNillNewsByUserQuery,
  useUpdateNillNewsMutation,
} from "../../store/api/apiSlice";

const baseUrl = process.env.REACT_APP_SERVER_URL;
const secretKey = process.env.REACT_APP_SECRET_KEY;

const NilNewsManagement = () => {
  const { data: nillNewsData = { news: [] }, refetch } =
    useFetchAllNillNewsQuery();
  const [createNillNews] = useCreateNillNewsMutation();
  const [deleteNillNews] = useDeleteNillNewsMutation();
  const [updateNillNews] = useUpdateNillNewsMutation();
  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isViewModalOpen, setViewModalOpen] = useState(false);
  const [currentNewsId, setCurrentNewsId] = useState(null);
  const [viewedNews, setViewedNews] = useState(null);
  const [filterParams, setFilterParams] = useState(null);
  const [image, setImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [schools, setSchools] = useState([]);

  const { data: filteredNillNewsData } = useFilterNillNewsByUserQuery(
    filterParams,
    { skip: !filterParams }
  );

  const titleRef = useRef(null);
  const categoryRef = useRef(null);
  const sourceRef = useRef(null);
  const tagsRef = useRef(null);
  const sportsTagsRef = useRef(null);
  const genderRef = useRef(null);
  const schoolNameRef = useRef(null);
  const schoolMascotRef = useRef(null);
  const detailsRef = useRef(null);
  const urlRef = useRef(null);
  const expiryDateRef = useRef(null);
  const userIdRef = useRef(null);
  const sportsRef = useRef(null);
  const schoolsRef = useRef(null);

  const genderOptions = ["male", "female", "other"];

  useEffect(() => {
    const fetchSchools = async () => {
      const xAuthToken =
        JSON.parse(localStorage.getItem("token") || "{}")?.token || "";
      const headers = {
        "secret-key": secretKey,
        "x-auth-token": xAuthToken,
      };

      try {
        const response = await fetch(`${baseUrl}/api/university/get-all`, {
          method: "GET",
          headers,
        });
        if (response.ok) {
          const data = await response.json();
          setSchools(data.university || []);
        } else {
          toast.error("Failed to fetch schools");
        }
      } catch (error) {
        toast.error("An error occurred while fetching schools");
      }
    };

    fetchSchools();
  }, []);

  const openModal = () => setModalOpen(true);
  const closeModal = () => {
    setModalOpen(false);
    setIsEditing(false);
    setImage(null);
    setImageFile(null);
    setCurrentNewsId(null);
  };

  const openViewModal = async (news) => {
    const xAuthToken = JSON.parse(localStorage.getItem("token"))?.token || "";
    try {
      const response = await fetch(
        `${baseUrl}/api/nil-news/get-single/${news._id}`,
        {
          headers: { "secret-key": secretKey, "x-auth-token": xAuthToken },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setViewedNews(data.news);
        setViewModalOpen(true);
      } else {
        toast.error("Failed to fetch news details.");
      }
    } catch (error) {
      toast.error("An error occurred while fetching news details.");
    }
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setViewedNews(null);
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImage(reader.result);
      reader.readAsDataURL(file);
      setImageFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nillNewsData = {
      title: titleRef.current?.value || "",
      category: categoryRef.current?.value || "",
      source: sourceRef.current?.value || "",
      tags: tagsRef.current?.value.split(","),
      sports_tags: sportsTagsRef.current?.value.split(","),
      gender: genderRef.current?.value || "",
      schoolName: schoolNameRef.current?.value || "",
      schoolMascot: schoolMascotRef.current?.value || "",
      details: detailsRef.current?.value || "",
      url: urlRef.current?.value || "",
      expiry_date: expiryDateRef.current?.value || "",
      image,
    };

    try {
      if (isEditing) {
        await updateNillNews({ id: currentNewsId, ...nillNewsData }).unwrap();
        toast.success("Nill news updated successfully!");
      } else {
        await createNillNews(nillNewsData).unwrap();
        toast.success("Nill news created successfully!");
      }
      refetch();
      closeModal();
    } catch (error) {
      toast.error(
        isEditing ? "Error updating nill news" : "Error creating nill news"
      );
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteNillNews(id).unwrap();
      toast.success("Nill news deleted successfully");
      refetch();
    } catch (error) {
      toast.error("Error deleting nill news");
    }
  };

  const handleEdit = (news) => {
    if (titleRef.current) titleRef.current.value = news.title || "";
    if (categoryRef.current) categoryRef.current.value = news.category || "";
    if (sourceRef.current) sourceRef.current.value = news.source || "";
    if (tagsRef.current) tagsRef.current.value = news.tags.join(", ");
    if (sportsTagsRef.current)
      sportsTagsRef.current.value = news.sports_tags.join(", ");
    if (genderRef.current) genderRef.current.value = news.gender || "";
    if (schoolNameRef.current)
      schoolNameRef.current.value = news.schoolName || "";
    if (schoolMascotRef.current)
      schoolMascotRef.current.value = news.schoolMascot || "";
    if (detailsRef.current) detailsRef.current.value = news.details || "";
    if (urlRef.current) urlRef.current.value = news.url || "";
    if (expiryDateRef.current)
      expiryDateRef.current.value = new Date(news.expiry_date)
        .toISOString()
        .split("T")[0];
    setImage(`${baseUrl}/${news.image}`);
    setCurrentNewsId(news._id);
    setIsEditing(true);
    openModal();
  };

  const handleFilter = (e) => {
    e.preventDefault();
    const filterData = {
      user_id: userIdRef.current?.value || "",
      sports: sportsRef.current?.value
        ? sportsRef.current?.value.split(",")
        : [],
      schools: schoolsRef.current?.value || "",
      gender: genderRef.current?.value || "",
    };
    setFilterParams(filterData);
  };

  const displayedNillNews = filterParams
    ? filteredNillNewsData?.news || []
    : nillNewsData.news;
  const paginatedUsers = displayedNillNews.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const totalPages = Math.ceil(displayedNillNews.length / itemsPerPage);
  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "22px",
        }}
      >
        <h3>Nill News Management</h3>
        <Button onClick={openModal}>Create Nill News</Button>
      </div>
      <Form onSubmit={handleFilter} className="mt-4 mb-4">
        <Row>
          <Col>
            <Form.Control placeholder="User ID" ref={userIdRef} />
          </Col>
          <Col>
            <Form.Control
              placeholder="Sports (comma separated)"
              ref={sportsRef}
            />
          </Col>
          <Col>
            <Form.Control placeholder="Schools" ref={schoolsRef} />
          </Col>
          <Col>
            <Form.Control placeholder="Gender" ref={genderRef} />
          </Col>
          <Col>
            <Button type="submit">Apply Filter</Button>
          </Col>
        </Row>
      </Form>

      <div style={{ overflowX: "auto" }}>
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Title</th>
              <th>Category</th>
              <th>Source</th>
              <th>Tags</th>
              <th>Sports Tags</th>
              <th>Gender</th>
              <th>School Name</th>
              <th>School Mascot</th>

              <th>URL</th>
              <th>Expiry Date</th>
              <th>Image</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedUsers && paginatedUsers.length > 0 ? (
              paginatedUsers.map((news) => (
                <tr
                  key={news._id}
                  onClick={() => openViewModal(news)}
                  style={{ cursor: "pointer" }}
                >
                  <td>{news.title}</td>
                  <td>{news.category}</td>
                  <td>{news.source}</td>
                  <td>{news.tags.join(", ")}</td>
                  <td>{news.sports_tags.join(", ")}</td>
                  <td>{news.gender}</td>
                  <td>{news.schoolName}</td>
                  <td>{news.schoolMascot}</td>

                  <td>
                    <a
                      href={news.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Link
                    </a>
                  </td>
                  <td>{new Date(news.expiry_date).toLocaleDateString()}</td>
                  <td>
                    {news.image && (
                      <Image
                        src={`${baseUrl}/${news.image}`}
                        alt="news"
                        width="50"
                        height="50"
                        thumbnail
                      />
                    )}
                  </td>
                  <td>
                    <div
                      style={{
                        display: "flex",
                        gap: "5px",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        className="Update-delete-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(news._id);
                        }}
                        width="30"
                        height="30"
                        src="https://img.icons8.com/ios-glyphs/30/filled-trash.png"
                        alt="filled-trash"
                      />
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="13" className="text-center">
                  No nill news available.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <div className="d-flex justify-content-center mt-3">
        {Array.from({ length: totalPages }, (_, index) => (
          <Button
            key={index}
            variant={currentPage === index + 1 ? "primary" : "outline-primary"}
            onClick={() => setCurrentPage(index + 1)}
            className="mx-1"
          >
            {index + 1}
          </Button>
        ))}
      </div>
      <CustomModal
        isOpen={isModalOpen}
        onClose={closeModal}
        className="scrollable-modal"
      >
        <div style={{ padding: "1rem", maxHeight: "80vh", overflowY: "auto" }}>
          <h4
            style={{
              fontSize: "1.2rem",
              fontWeight: "bold",
              textAlign: "center",
              marginBottom: "20px",
            }}
          >
            {isEditing ? "Edit Nill News" : "Create Nill News"}
          </h4>
          <CustomForm onSubmit={handleSubmit}>
            <Form.Group>
              <Form.Label>Title</Form.Label>
              <Form.Control ref={titleRef} required />
            </Form.Group>
            <Form.Group>
              <Form.Label>Category</Form.Label>
              <Form.Control ref={categoryRef} required />
            </Form.Group>
            <Form.Group>
              <Form.Label>Source</Form.Label>
              <Form.Control ref={sourceRef} required />
            </Form.Group>
            <Form.Group>
              <Form.Label>Tags</Form.Label>
              <Form.Control ref={tagsRef} required />
            </Form.Group>
            <Form.Group>
              <Form.Label>Sports Tags</Form.Label>
              <Form.Control ref={sportsTagsRef} required />
            </Form.Group>
            <Form.Group>
              <Form.Label>Gender</Form.Label>
              <Form.Select ref={genderRef} required>
                <option value="">Select Gender</option>
                {genderOptions.map((gender) => (
                  <option key={gender} value={gender}>
                    {gender}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>School Name</Form.Label>
              <Form.Select ref={schoolNameRef} required>
                <option value="">Select School</option>
                {schools.map((school) => (
                  <option key={school._id} value={school.name}>
                    {school.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>School Mascot</Form.Label>
              <Form.Control ref={schoolMascotRef} required />
            </Form.Group>
            <Form.Group>
              <Form.Label>Details</Form.Label>
              <Form.Control as="textarea" ref={detailsRef} required />
            </Form.Group>
            <Form.Group>
              <Form.Label>URL</Form.Label>
              <Form.Control ref={urlRef} required />
            </Form.Group>
            <Form.Group>
              <Form.Label>Expiry Date</Form.Label>
              <Form.Control type="date" ref={expiryDateRef} required />
            </Form.Group>
            <Form.Group>
              <Form.Label>Image</Form.Label>
              <Form.Control type="file" onChange={handleImageChange} />
              {image && (
                <Image src={image} alt="Preview" width="100" className="mt-2" />
              )}
            </Form.Group>
            <div className="d-flex justify-content-end mt-3">
              <Button variant="secondary" onClick={closeModal} className="me-2">
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {isEditing ? "Update" : "Save"}
              </Button>
            </div>
          </CustomForm>
        </div>
      </CustomModal>

      <CustomModal isOpen={isViewModalOpen} onClose={closeViewModal}>
        <div
          style={{
            maxWidth: "600px",
            padding: "20px",
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          <h3>Nill News Details</h3>
          <p>
            <strong>Title:</strong> {viewedNews?.title}
          </p>
          <p>
            <strong>Category:</strong> {viewedNews?.category}
          </p>
          <p>
            <strong>Source:</strong> {viewedNews?.source}
          </p>
          <p>
            <strong>Tags:</strong> {viewedNews?.tags.join(", ")}
          </p>
          <p>
            <strong>Sports Tags:</strong> {viewedNews?.sports_tags.join(", ")}
          </p>
          <p>
            <strong>Gender:</strong> {viewedNews?.gender}
          </p>
          <p>
            <strong>School Name:</strong> {viewedNews?.schoolName}
          </p>
          <p>
            <strong>School Mascot:</strong> {viewedNews?.schoolMascot}
          </p>
          <p>
            <strong>Details:</strong> {viewedNews?.details}
          </p>
          <p>
            <strong>Expiry Date:</strong>{" "}
            {new Date(viewedNews?.expiry_date).toLocaleDateString()}
          </p>
          {viewedNews?.image && (
            <div className="mt-2">
              <p>
                <strong>Image:</strong>
              </p>
              <Image
                src={`${baseUrl}/${viewedNews.image}`}
                alt="news"
                thumbnail
                width="200"
              />
            </div>
          )}
        </div>
      </CustomModal>
    </div>
  );
};

export default NilNewsManagement;
